import React, { useState, useRef, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';

import StyledCrossTeasers from './crossTeasers.style';
import Container from '../../layout/container/container';
import TiltedTeaser from '../../molecules/tilted-teaser/tiltedTeaser';
import ArticleTeaser from '../../molecules/article-teaser/articleTeaser';
import EventTeaser from '../../molecules/event-teaser/eventTeaser';
import Headline from '../../atoms/headline/headline';

const BasicTeaser = dynamic(() => import('../../molecules/basic-teaser/basicTeaser'));

const renderTeaser = (teaser: Teaser) => {
  const byType = {
    event: <EventTeaser size="s" {...teaser} />,
    article: <ArticleTeaser size="m" {...teaser} />,
    basicTeaser: <BasicTeaser {...teaser} size="m" orientation={teaser.imageFormat || null} />,
    staticPage: <BasicTeaser {...teaser} size="m" orientation={teaser.imageFormat || null} />,
    landingpageSimple: <BasicTeaser {...teaser} size="m" orientation={teaser.imageFormat || null} />
  };

  return byType[teaser.teaserType] || byType.basicTeaser;
};

type Teaser = {
  id: number;
  slug: string;
  uri: string;
  headline: string;
  subline: string;
  label?: string;
  teaserType?: string;
  time?: string;
  categories: string[];
  pageTags: string[];
  copy: string;
  image: object;
  imageFormat: string;
};

type Props = {
  headline?: string;
  data: Teaser[];
};

type Status = 'entering' | 'entered' | 'exiting' | 'exited';

const TRANSITION_MS = 400;

const CrossTeasers = ({ headline, data, ...props }: Props) => {
  const [firstTeaser, ...tiltedTeasers] = data;
  const activeRef = useRef(2);
  const initialActive = tiltedTeasers.length;

  const [statuses, setStatuses] = useState<Record<number, Status>>(() =>
    tiltedTeasers.reduce(
      (acc, _, i) => {
        const position = i + 1;
        acc[position] = position === initialActive ? 'entered' : 'exited';
        return acc;
      },
      {} as Record<number, Status>
    )
  );

  const timers = useRef<number[]>([]);

  const handleTeaserSwap = useCallback((e: MouseEvent, id: number) => {
    const prev = activeRef.current;
    if (id === prev) return;
    if (e) e.preventDefault();
    activeRef.current = id;

    setStatuses(s => ({ ...s, [id]: 'entering', [prev]: 'exiting' }));
    const timer = window.setTimeout(() => {
      setStatuses(s => ({ ...s, [id]: 'entered', [prev]: 'exited' }));
    }, TRANSITION_MS);
    timers.current.push(timer);
  }, []);

  useEffect(() => () => timers.current.forEach(t => window.clearTimeout(t)), []);

  return (
    <StyledCrossTeasers {...props}>
      {headline && (
        <Container>
          <Headline tag="h3" secondary className="crossTeasers__headline">
            {headline}
          </Headline>
        </Container>
      )}

      {firstTeaser.uri && (
        <Container className="crossTeasers__first-teaser">{renderTeaser(firstTeaser)}</Container>
      )}

      <div className="crossTeasers__other-teasers">
        {tiltedTeasers.map((tiltedTeaser, index) => {
          const position = index + 1;
          const state = statuses[position];

          return (
            <TiltedTeaser
              key={index}
              teaserPosition={position}
              data-teaser-position={position}
              className={`crossTeasers__teaser ${state}`}
              onMouseEnter={e => handleTeaserSwap(e, position)}
              onMouseLeave={e => handleTeaserSwap(e, position)}
              onTouchStart={e => handleTeaserSwap(e, position)}
              transitionState={state}
              {...tiltedTeaser}
            />
          );
        })}
      </div>
    </StyledCrossTeasers>
  );
};

export default CrossTeasers;
